import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Package, ShoppingCart, Loader2, X, CheckCircle } from "lucide-react";
import useIsMobileGrid from "../hooks/useMedia";
import { useCart } from "../shared/contexts/CartContext";
import { Button } from "./ui/button";

const SlickSlider = Slider?.default?.default ?? Slider?.default ?? Slider;

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value ?? 0);

const getPlanoTheme = (nome) => {
  if (!nome) return { top: "bg-warm-100", btn: "bg-rose-400 hover:brightness-105", badge: "bg-warm-200 text-warm-700" };
  const n = nome.toLowerCase();
  if (n.includes("essencial")) return { top: "bg-slate-blue-400", btn: "bg-slate-blue-400 hover:brightness-110", badge: "bg-white/20 text-white" };
  if (n.includes("confort"))   return { top: "bg-rose-400",       btn: "bg-rose-400 hover:brightness-110",       badge: "bg-white/20 text-white" };
  if (n.includes("premium"))   return { top: "bg-amber-500",      btn: "bg-amber-500 hover:brightness-110",      badge: "bg-white/20 text-white" };
  return { top: "bg-warm-100", btn: "bg-rose-400 hover:brightness-105", badge: "bg-warm-200 text-warm-700" };
};

const formatDescription = (text) => {
  if (!text) return null;
  let descriptionText = "";
  if (typeof text === "object" && text !== null) {
    descriptionText = text.ideal_para || text.texto || text.chamada || "";
  } else if (typeof text === "string") {
    descriptionText = text;
  }
  return descriptionText.split("\n").map((line, index, arr) => (
    <React.Fragment key={index}>
      {line}
      {index < arr.length - 1 && <br />}
    </React.Fragment>
  ));
};

const CompraPlanos = ({ userId }) => {
  const navigate = useNavigate();
  const isGrid = useIsMobileGrid();
  const { addItem } = useCart();
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [addedItemName, setAddedItemName] = useState("");

  useEffect(() => {
    const fetchPlanosAtivos = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from("planos")
          .select("*")
          .eq("tipo_usuario", "baba")
          .eq("ativo", true)
          .order("valor", { ascending: true });
        if (error) throw error;
        setPlanos(data || []);
      } catch (err) {
        console.error("Erro ao buscar planos:", err);
        setError("Não foi possível carregar os planos de assinatura.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlanosAtivos();
  }, []);

  const handleAddToCart = (plano) => {
    if (!userId) { setError("Você precisa estar logado para comprar."); return; }

    if (!plano.qrcode) console.error(`[CompraPlanos] ERRO: O plano "${plano.nome}" (ID: ${plano.id}) não possui QR Code.`);
    if (!plano.link_pgto) console.error(`[CompraPlanos] ERRO: O plano "${plano.nome}" (ID: ${plano.id}) não possui PIX.`);

    let qrCodeUrl = plano.qrcode;
    if (qrCodeUrl && !qrCodeUrl.startsWith("http")) {
      const { data } = supabase.storage.from("qrcode").getPublicUrl(qrCodeUrl);
      qrCodeUrl = data.publicUrl;
    }

    addItem({
      id: plano.id,
      tipo: "assinatura",
      nome: plano.nome,
      preco: plano.valor,
      descricao: plano.descricao,
      periodo: plano.periodo,
      pix_chave: plano.chave_pix,
      pix_copia_cola: plano.link_pgto,
      pix_qrcode_url: qrCodeUrl,
    });

    setAddedItemName(plano.nome);
    setShowModal(true);
  };

  const sliderSettings = {
    dots: true,
    infinite: planos.length > 2,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 768,  settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
    className: "pacotes-carousel-compra",
  };

  const PlanoCard = ({ plano, onComprar }) => {
    const theme = getPlanoTheme(plano.nome);
    const isPremium = plano.nome?.toLowerCase().includes("premium");
    return (
      <div
        id={`cp-plan-card-${plano.id}`}
        className={`mx-2 flex flex-col rounded-2xl overflow-hidden border border-warm-100 shadow-sm bg-white ${isPremium ? "ring-2 ring-amber-400" : ""}`}
      >
        {/* Topo colorido */}
        <div id={`cp-plan-top-${plano.id}`} className={`${theme.top} px-5 pt-5 pb-6 text-white`}>
          <span id={`cp-plan-badge-${plano.id}`} className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide mb-3 ${theme.badge}`}>
            {isPremium ? "VIP" : "PLANO"}
          </span>
          <h3 id={`cp-plan-name-${plano.id}`} className="text-lg font-bold leading-tight mb-1">{plano.nome}</h3>
          <p id={`cp-plan-price-${plano.id}`} className="text-3xl font-extrabold">
            {formatCurrency(plano.valor)}
            <span id={`cp-plan-freq-${plano.id}`} className="text-sm font-normal opacity-80">/mês</span>
          </p>
        </div>

        {/* Linha de separação estilo ticket */}
        <div id={`cp-plan-divider-${plano.id}`} className="flex items-center px-4">
          <div className="h-3 w-3 -ml-4 rounded-full bg-warm-50" />
          <div className="flex-1 border-t-2 border-dashed border-warm-200 mx-1" />
          <div className="h-3 w-3 -mr-4 rounded-full bg-warm-50" />
        </div>

        {/* Corpo */}
        <div id={`cp-plan-body-${plano.id}`} className="flex flex-col flex-1 px-5 py-4 gap-4">
          <div id={`cp-plan-desc-box-${plano.id}`} className="text-sm text-warm-600 leading-relaxed flex-1">
            {formatDescription(plano.descricao) || <p>Vantagens exclusivas para babás.</p>}
          </div>
          <button
            id={`cp-btn-buy-${plano.id}`}
            className={`w-full rounded-xl py-2.5 text-sm font-semibold text-white transition ${theme.btn}`}
            onClick={() => onComprar(plano)}
          >
            Assinar Agora
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div id="cp-status-loading" className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 id="cp-loading-icon" size={20} className="animate-spin" />
        <span>Carregando planos...</span>
      </div>
    );
  }

  return (
    <section id="cp-main-section" className="flex flex-col items-center w-full py-4">
      <h2 id="cp-main-heading" className="flex items-center gap-2 text-xl font-bold text-foreground mb-1">
        <Package id="cp-package-icon" size={22} className="text-rose-400" />
        Planos de Assinatura
      </h2>
      <p id="cp-main-subtext" className="text-sm text-muted-foreground text-center mb-5">
        Assine um plano para destacar seu perfil e ganhar mais visibilidade.
      </p>

      {error && (
        <div id="cp-error-msg" className="w-full rounded-xl bg-destructive/10 border border-destructive/20 px-4 py-2.5 text-sm text-destructive mb-3">
          {error}
        </div>
      )}

      {planos.length > 0 ? (
        isGrid ? (
          <div id="cp-plans-grid" className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {planos.map((plano) => (
              <PlanoCard key={plano.id} plano={plano} onComprar={handleAddToCart} />
            ))}
          </div>
        ) : (
          <div className="w-full">
            <SlickSlider id="cp-plans-slider" {...sliderSettings}>
              {planos.map((plano) => (
                <PlanoCard key={plano.id} plano={plano} onComprar={handleAddToCart} />
              ))}
            </SlickSlider>
          </div>
        )
      ) : (
        !loading && <p className="text-sm text-muted-foreground">Nenhum plano de assinatura disponível no momento.</p>
      )}

      {/* Modal de Sucesso */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <div className="relative bg-background rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowModal(false)} className="absolute top-3 right-3 flex items-center justify-center h-8 w-8 rounded-full bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors">
              <X size={18} />
            </button>
            <div className="flex justify-center mb-4">
              <CheckCircle size={48} className="text-emerald-500" />
            </div>
            <h3 className="text-lg font-bold text-foreground mb-2">Adicionado ao Carrinho!</h3>
            <p className="text-sm text-muted-foreground mb-5">
              <strong>{addedItemName}</strong> foi adicionado com sucesso.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowModal(false)}>
                Continuar
              </Button>
              <Button className="flex-1 gap-2 bg-emerald-500 hover:bg-emerald-600" onClick={() => navigate("/pagamento")}>
                <ShoppingCart size={16} /> Carrinho
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CompraPlanos;
